import React, { useState } from 'react';
import { Book, Code, Shield, Zap, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import Icon from '../../../components/AppIcon';


export default function SecuritySettingsGuide() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'how-it-works', label: 'How It Works', icon: Zap },
    { id: 'permissions', label: 'Permission Levels', icon: Shield },
    { id: 'examples', label: 'Examples', icon: Code }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {sections?.map(section => {
            const Icon = section?.icon;
            return (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`${
                  activeSection === section?.id
                    ? 'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
              >
                <Icon className="h-4 w-4" />
                {section?.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="p-6">
        {activeSection === 'overview' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Dynamic Security Settings</h2>
              <p className="text-gray-600 leading-relaxed">
                The Dynamic Security Settings system allows Super Admins and Business Admins to control
                feature access for each role in real-time, without code changes or database migrations.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">Key Benefits:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Real-time permission changes (no code deployment needed)</li>
                    <li>Granular control at feature level</li>
                    <li>Tenant-specific overrides for multi-tenant flexibility</li>
                    <li>Complete audit trail of all permission changes</li>
                    <li>Backward compatible with existing role_permissions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900">Super Admin Control</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Super Admins can manage global security settings that apply across all organizations.
                  These settings serve as the default baseline for all tenants.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900">Tenant Overrides</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Business Admins can create tenant-specific overrides that take precedence over global
                  settings, allowing customization per organization.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'how-it-works' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How It Works</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                The system uses a three-tier permission checking mechanism:
              </p>
            </div>

            <div className="space-y-4">
              <div className="border-l-4 border-indigo-600 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">1. Tenant-Specific Override Check</h3>
                <p className="text-sm text-gray-600">
                  First, the system checks if a tenant-specific override exists for the role-feature
                  combination. If found, this setting takes precedence over all others.
                </p>
                <div className="mt-2 bg-gray-50 rounded p-3 text-xs font-mono text-gray-800">
                  security_settings WHERE role_id = ? AND feature = ? AND tenant_id = ?
                </div>
              </div>

              <div className="border-l-4 border-purple-600 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">2. Global Setting Check</h3>
                <p className="text-sm text-gray-600">
                  If no tenant override exists, check for a global setting (tenant_id IS NULL).
                  Global settings apply to all organizations.
                </p>
                <div className="mt-2 bg-gray-50 rounded p-3 text-xs font-mono text-gray-800">
                  security_settings WHERE role_id = ? AND feature = ? AND tenant_id IS NULL
                </div>
              </div>

              <div className="border-l-4 border-green-600 pl-4">
                <h3 className="font-semibold text-gray-900 mb-2">3. Backward Compatibility Fallback</h3>
                <p className="text-sm text-gray-600">
                  If no security_settings entry exists, fall back to the legacy role_permissions table.
                  This ensures existing permissions continue to work.
                </p>
                <div className="mt-2 bg-gray-50 rounded p-3 text-xs font-mono text-gray-800">
                  role_permissions WHERE role_id = ? AND feature = ? AND access_level != 'none'
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-900">
                  <p className="font-semibold mb-2">Important Notes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Changes take effect immediately for all users</li>
                    <li>All changes are logged in the audit_logs table</li>
                    <li>Super Admins bypass all permission checks</li>
                    <li>Tenant overrides only affect that specific organization</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'permissions' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Permission Levels</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Security settings use a simple boolean model: features are either enabled or disabled
                for each role.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <h3 className="font-semibold text-green-900">Enabled (Allowed)</h3>
                </div>
                <p className="text-sm text-green-800 mb-3">
                  When a feature is enabled for a role:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
                  <li>Users with that role can access the feature</li>
                  <li>Navigation items for the feature are visible</li>
                  <li>API calls to the feature succeed</li>
                  <li>UI components for the feature render</li>
                </ul>
              </div>

              <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-6 w-6 text-red-600" />
                  <h3 className="font-semibold text-red-900">Disabled (Denied)</h3>
                </div>
                <p className="text-sm text-red-800 mb-3">
                  When a feature is disabled for a role:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                  <li>Users with that role cannot access the feature</li>
                  <li>Navigation items are hidden or disabled</li>
                  <li>API calls return permission denied errors</li>
                  <li>UI components show access denied messages</li>
                </ul>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Feature
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Typical Access
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Services</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Service configuration and management</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Admin, Manager, Staff</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Users</td>
                    <td className="px-6 py-4 text-sm text-gray-600">User management and role assignment</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Admin, Manager</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Billing</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Billing, invoices, and payments</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Admin only</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Reports</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Analytics and reporting</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Admin, Manager</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Settings</td>
                    <td className="px-6 py-4 text-sm text-gray-600">System configuration</td>
                    <td className="px-6 py-4 text-sm text-gray-600">Admin only</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeSection === 'examples' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Usage Examples</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Here are practical examples of how to use the dynamic security settings system.
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Example 1: Disable Service Creation for Staff</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Scenario: You want Staff members to view services but not create new ones.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                    <li>Navigate to Role Management Dashboard → Security Settings tab</li>
                    <li>Find the "Staff" role column</li>
                    <li>Locate the "Services" feature row</li>
                    <li>Uncheck the checkbox to disable access</li>
                    <li>Changes apply immediately to all Staff users</li>
                  </ol>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Example 2: Create Tenant-Specific Override</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Scenario: One organization wants Managers to access Billing, but globally Managers cannot.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                    <li>Log in as Business Admin for that organization</li>
                    <li>Navigate to Security Settings</li>
                    <li>Enable "Billing" for "Manager" role</li>
                    <li>This creates a tenant override (marked with "Override" label)</li>
                    <li>Only affects Managers in that specific organization</li>
                  </ol>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Example 3: Bulk Enable/Disable</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Scenario: Quickly enable all features for a new custom role.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                    <li>Select the role from the dropdown</li>
                    <li>Click "Enable All" button</li>
                    <li>All features are enabled for that role instantly</li>
                    <li>Use "Disable All" to revoke all permissions</li>
                    <li>Use "Reset to Defaults" to restore original settings</li>
                  </ol>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Example 4: Audit Permission Changes</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Scenario: Review who changed permissions and when.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">Steps:</p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                    <li>Navigate to Audit Trail page</li>
                    <li>Filter by action type: "security_setting_updated"</li>
                    <li>View complete history with before/after states</li>
                    <li>See who made changes and when</li>
                    <li>Export audit logs for compliance reporting</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}